import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { apiFetch } from '../api/client';
import type { LatestPullRequest, PublishResult, SyncStatus } from '../api/types';

type DisplayState =
  | { kind: 'loading' }
  | { kind: 'publishing' }
  | { kind: 'in-progress'; pr: LatestPullRequest }
  | { kind: 'no-changes' }
  | { kind: 'publishable' };

const resolveState = (sync: SyncStatus | undefined, publishing: boolean): DisplayState => {
  if (sync === undefined) return { kind: 'loading' };
  if (publishing) return { kind: 'publishing' };
  const pr = sync.latestPullRequest;
  if (pr !== null && pr.state === 'open') return { kind: 'in-progress', pr };
  if (sync.branchAhead > 0) return { kind: 'publishable' };
  return { kind: 'no-changes' };
};

export const PublishButton = () => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{
    type: 'ok' | 'warn' | 'error';
    text: string;
    url?: string;
  } | null>(null);

  const { data: sync } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => apiFetch<SyncStatus>('/api/sync'),
  });

  const publish = useMutation({
    mutationFn: () => apiFetch<PublishResult>('/api/publish', { method: 'POST' }),
    onSuccess: (res) => {
      if (res.status === 'merge-failed') {
        setMessage({
          type: 'warn',
          text: `PR #${res.pullRequestNumber.toString()} は作成しましたが auto-merge 設定に失敗しました (${res.error})。GitHub から手動でマージしてください`,
          url: res.pullRequestUrl,
        });
      } else {
        setMessage({
          type: 'ok',
          text: `PR #${res.pullRequestNumber.toString()} を作成しました (auto-merge 有効)`,
          url: res.pullRequestUrl,
        });
      }
      void queryClient.invalidateQueries();
    },
    onError: (e: Error) => {
      setMessage({ type: 'error', text: e.message });
    },
  });

  const state = resolveState(sync, publish.isPending);

  const onPublish = () => {
    if (
      !window.confirm(
        'main への PR を作成して auto-merge します。本番リポジトリに反映されますが、よろしいですか?',
      )
    ) {
      return;
    }
    setMessage(null);
    publish.mutate();
  };

  const messageColor =
    message?.type === 'ok'
      ? 'text-green-700'
      : message?.type === 'warn'
        ? 'text-yellow-700'
        : 'text-red-700';

  return (
    <div className="relative">
      {state.kind === 'loading' && (
        <button
          type="button"
          disabled
          className="inline-flex h-8 items-center rounded bg-gray-900 px-3 text-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          公開する
        </button>
      )}
      {state.kind === 'publishing' && (
        <button
          type="button"
          disabled
          className="inline-flex h-8 items-center rounded bg-gray-900 px-3 text-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          公開中...
        </button>
      )}
      {state.kind === 'in-progress' && (
        <a
          href={state.pr.url}
          target="_blank"
          rel="noreferrer"
          title="レビュー / マージ待ちです。クリックで PR を開きます"
          className="inline-flex h-8 items-center rounded border border-amber-300 bg-amber-50 px-3 text-xs font-medium text-amber-900 hover:bg-amber-100"
        >
          処理中 (#{state.pr.number})
        </a>
      )}
      {state.kind === 'no-changes' && (
        <button
          type="button"
          disabled
          title="main との差分がないため公開できません"
          className="inline-flex h-8 items-center rounded bg-gray-900 px-3 text-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          差分なし
        </button>
      )}
      {state.kind === 'publishable' && (
        <button
          type="button"
          onClick={onPublish}
          className="inline-flex h-8 items-center rounded bg-gray-900 px-3 text-xs font-medium text-white hover:bg-gray-800"
        >
          公開する
        </button>
      )}
      {message !== null && (
        <div className="absolute top-full right-0 z-40 mt-1 w-72 rounded border bg-white p-2 shadow-lg">
          <div className="flex items-start gap-2">
            <p className={`flex-1 text-xs ${messageColor}`}>
              {message.text}
              {message.url !== undefined && (
                <>
                  {' '}
                  <a href={message.url} target="_blank" rel="noreferrer" className="underline">
                    PR を開く
                  </a>
                </>
              )}
            </p>
            <button
              type="button"
              onClick={() => setMessage(null)}
              className="text-gray-400 hover:text-gray-700"
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
